import React, { useState } from "react";
import { Box, Button, Typography, Dialog, DialogContent } from "@mui/material";
import bgImage from "../../../assets/media/become-provider-bg.jpg";
import CustomVideoPlayer from "./custom-video-player";
import serviceProviderImg from "../../../assets/media/service-provider.jpg";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../constants/routes";
import videoFile from "../../../assets/media/build-together-sound.mp4";
import { Close } from "@mui/icons-material";

const videoURL = videoFile;

function BuildTogetherSection() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "center",
        padding: { xs: "1.5rem", md: "2rem" },
        gap: { xs: "1.5rem", md: "2rem" },
        maxHeight: { xs: "none", md: 400, lg: 769 },
        overflow: "hidden",
        fontFamily: "Roboto",
        color: "#FFFFFF",
        "& > *": { position: "relative", zIndex: 1 },
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `
            linear-gradient(rgba(16, 65, 69, 0.9), rgba(16, 65, 69, 0.9)),
            url(${bgImage})
          `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          zIndex: 0,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          opacity: 0.04,
          pointerEvents: "none",
          zIndex: 1,
        },
      }}
    >
      <Box sx={{ width: { xs: "100%", md: "40%" } }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            fontSize: { xs: "24px", md: "32px", lg: "45px" },
            mb: 2,
            lineHeight: { xs: "32px", md: "36px", lg: "59px" },
            textAlign: { xs: "center", md: "left" },
          }}
        >
          Grow Your Business Presence on <span style={{ color: "#9F7F2C" }}>MemorialGo</span>
        </Typography>

        <Typography
          variant="body1"
          sx={{
            mb: 4,
            mt: { xs: 2, md: 4 },
            fontSize: { xs: "14px", md: "16px", lg: "18px" },
            textAlign: { xs: "center", md: "left" },
            lineHeight: { xs: "22px", md: "24px" },
            width: { xs: "100%", md: "90%" },
            mx: { xs: "auto", md: 0 },
          }}
        >
          Join a trusted platform that connects service providers with families actively seeking funeral and memorial services — helping increase visibility, generate leads, and present services through a structured experience.
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" },
            marginTop: "12px",
            width: "100%",
            gap: "1rem",
          }}
        >
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#fff",
              padding: "12px 15px",
              borderRadius: "8px",
              fontSize: { xs: "14px", md: "16px" },
              fontWeight: 600,
              color: "#212529",
              textTransform: "none",
              "&:hover": { backgroundColor: "#f0f0f0" },
            }}
            onClick={() => navigate(routes.BECOME_PARTNER)}
          >
            Join as a Partner
          </Button>
          <Typography
            sx={{
              fontSize: "13px",
              color: "#e5e5e5",
              textAlign: { xs: "center", md: "left" },
            }}
          >
            Already have an account?{" "}
            <Box
              component="span"
              sx={{
                color: "#ffffff",
                fontWeight: 600,
                marginRight: "1rem",
                marginLeft: "0.25rem",
                cursor: "pointer",
                textDecoration: "underline",
                "&:hover": {
                  opacity: 0.85,
                },
              }}
              onClick={() => navigate(routes.PROVIDER_LOGIN)}
            >
              Provider Login
            </Box>
            <Box
              component="span"
              sx={{
                color: "#ffffff",
                fontWeight: 600,
                cursor: "pointer",
                textDecoration: "underline",
                "&:hover": {
                  opacity: 0.85,
                },
              }}
              onClick={() => navigate(routes.PRENEED_LOGIN)}
            >
              Preneed Planner Login
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* Video thumbnail section */}
      <Box
        sx={{
          width: { xs: "100%", md: "60%", lg: "840px" },
          position: "relative",
          borderRadius: "50px",
          overflow: "hidden",
        }}
      >
        <img
          src={serviceProviderImg}
          alt="Video Thumbnail"
          style={{
            width: "100%",
            height: "auto",
            display: "block",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            opacity: 1,
            transition: "opacity 0.3s ease",
            "&:hover": { opacity: 1 },
            bottom: 20,
          }}
        >
          <Button
            variant="contained"
            disableRipple
            sx={{
              backgroundColor: "#FFEDBE",
              color: "#104145",
              fontWeight: 600,
              textTransform: "none",
              padding: "10px 20px",
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#b8952f" },
            }}
            onClick={handleOpenModal}
          >
            Watch Video
          </Button>
        </Box>
      </Box>

      <Dialog
        open={isModalOpen}
        onClose={() => {
          handleCloseModal();
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent
          sx={{
            p: 0,
            bgcolor: "black",
            border: "none",
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              color: "white",
              zIndex: 2,
              fontWeight: 700,
            }}
          >
            Building Success Together
          </Typography>
          <Button
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "white",
              zIndex: 2,
              fontWeight: 700,
              "&:hover": { transform: "scale(1.1)" },
            }}
          >
            <Close fontSize="medium" />
          </Button>
          <CustomVideoPlayer src={videoURL} />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default BuildTogetherSection;
